import React, { useMemo, Component } from "react";
import { Radio } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { SBTypography, SBBox } from "modules/Commons";
import { useTranslation } from "react-i18next";
import { IAvailableModel } from "modules/ProductModelAnalyser/models/IAvailableModel";
import SBDataTable from "modules/Commons/SBDataDisplays/SBTable/SBDataTable";

import { IProductModelSelectionTableProps } from "../models/IProductModelSelectionTableProps";

const ProductModelSelectionTable: React.FC<IProductModelSelectionTableProps> = ({ availableModels, selectedModels = [], onSelectModel }) => {
	const { t } = useTranslation();

	const rows = useMemo(() => {
		if (!availableModels) return [];
		
		const grouped: Record<string, any[]> = {};
		
		availableModels.forEach(model => {
			const parts = model?.code?.split(":");
			const typeName = parts && parts.length > 1 ? parts[1] : t("others");
			
			if (!grouped[typeName]) {
				grouped[typeName] = [];
			}
			grouped[typeName].push(model);
		});
		
		return Object.entries(grouped).map(([type, models], index) => ({
			id: `group_${index}`,
			type: type,
			models: models
		}));
	}, [availableModels, t]);

	const columns = useMemo(() => {
		return [
			{
				id: (row: any) => row,
				key: "select_primary",
				name: t("forecast", "Forecast"),
				width: "auto",
				style: { width: "1%", whiteSpace: "nowrap", padding: "0 16px", textAlign: "center" },
				formatter: (row: any) => {
					if (!row) return "";
					if (row.models) {
						return (
							<SBBox display="flex" alignItems="center" justifyContent="center">
								<KeyboardArrowRightIcon fontSize="small" color="action" />
							</SBBox>
						);
					}
					
					if (row.code) {
						const isSelected = selectedModels[0] === row.code;
						return (
							<SBBox display="flex" alignItems="center" justifyContent="center">
								<Radio 
									checked={isSelected}
									color="primary" // Purple by default theme primary
									onChange={() => onSelectModel(row.code, true)}
									onClick={(e) => e.stopPropagation()}
									size="small"
								/>
							</SBBox>
						);
					}
					return "";
				}
			},
			{
				id: (row: any) => row,
				key: "select_secondary",
				name: t("benchmark", "Benchmark"),
				width: "auto",
				style: { width: "1%", whiteSpace: "nowrap", padding: "0 16px", textAlign: "center" },
				formatter: (row: any) => {
					if (!row || row.models) return "";
					
					if (row.code) {
						const isSelected = selectedModels[1] === row.code;
						return (
							<SBBox display="flex" alignItems="center" justifyContent="center">
								<Radio 
									checked={isSelected}
									color="warning" // Yellow by default theme warning
									onChange={() => onSelectModel(row.code, false)}
									onClick={(e) => e.stopPropagation()}
									size="small"
								/>
							</SBBox>
						);
					}
					return "";
				}
			},
			{
				id: (row: any) => row,
				key: "code",
				name: t("code"),
				width: "auto",
				style: { width: "1%", whiteSpace: "nowrap", padding: "0 16px" },
				formatter: (row: any) => {
					if (!row) return "";
					if (row.models) {
						return (
							<SBTypography variant="subtitle2" fontWeight={600} color="textSecondary">
								{t("modelType", { type: row.type })} ({row.models.length})
							</SBTypography>
						);
					}
					return (
						<SBTypography variant="caption" sx={{ fontFamily: "monospace", fontSize: "12px", wordBreak: "break-all" }}>
							{row.code}
						</SBTypography>
					);
				}
			},
			{
				id: (row: any) => row,
				key: "description",
				name: t("description"),
				width: "100%",
				style: { width: "100%", padding: "0 16px" },
				formatter: (row: any) => {
					if (!row || row.models) return "";
					return (
						<SBTypography variant="body2" color="textSecondary" sx={{ fontSize: "13px" }}>
							{row.description}
						</SBTypography>
					);
				}
			}
		];
	}, [selectedModels, onSelectModel, t]);

	return (
		<SBBox mt={0} mb={0} sx={{ 
			"& .sb-table": { border: "1px solid rgba(0,0,0,.1)", borderRadius: "8px", boxShadow: "none" },
			"& .MuiTableHead-root": { display: "table-header-group" }, // Show headers now since we have two distinct columns
			"& .MuiTable-root": { tableLayout: "auto" }
		}}>
			<SBDataTable
				rows={rows}
				columns={columns}
				collapseFieldName="models"
				selectable={false}
				title={t("models", "Modelos")}
				toolbarContent={
					<SBTypography variant="body2" color="textSecondary" fontWeight={500}>
						{t("selectModelsToCompare", "Selecione até 2 para comparar")}
					</SBTypography>
				}
				showHeader={true}
				pagination={false}
				striped={false}
				noEmptyRows={true}
				fullWidth={true}
			/>
		</SBBox>
	);
};

export default ProductModelSelectionTable;
